"use client";

import React, { useState } from "react";
import { useSelector } from "react-redux";
import { ChevronDownIcon, Divider } from "staysure-component-library";

import Accordion from "@/components/atoms/accordion/accordion";
import selectedColorBrand from "@/config/tailwind-colors/brand-colors";
import useGtmViewQuoteData from "@/hooks/gtm/use-gtm-view-quote";
import { RenewalYourPolicyDetailsAreaContentType } from "@/schema/renewal-page/confirmation-page.shema";
import { RootState } from "@/store/root-reducer";
import {
  formatTelephoneNumber,
  rearrangeDateFormat,
} from "@/utils/confirmation.utils";
import QuoteDataMap from "@/utils/gtm/quote-data-map";

function ConfirmationPolicyDetails({
  yourPolicyDetailTitle,
  yourPolicyNameText,
  orderTextDate,
  typeOfCoverText,
  coverDatesText,
  policyCostText,
  emailAdressText,
  phoneNumberText,
}: Readonly<RenewalYourPolicyDetailsAreaContentType>) {
  const { referenceId, attributes } = useSelector(
    (state: RootState) => state.quote
  );
  const [expanded, setExpanded] = useState(true);

  const telephoneNumber = formatTelephoneNumber(
    attributes?.organiser?.telephone
  );

  let gtmQuoteViewData = null;
  if (referenceId) {
    gtmQuoteViewData = QuoteDataMap(
      attributes,
      "renewal_purchase",
      "renewal confirmation",
      referenceId,
      "renewal_journey"
    );
    delete gtmQuoteViewData.destination;
    delete gtmQuoteViewData.trip_extra;
  }

  useGtmViewQuoteData({
    quoteData: {
      ...gtmQuoteViewData,
      renewal_transaction_id:
        attributes.gtmEvents?.renewalTransactionId ?? null,
      renewal_payment_method:
        attributes.gtmEvents?.renewalPaymentMethod ?? null,
      voucher_code: attributes.paymentDetails?.discounts.voucher ?? null,
      currency: "GBP",
      auto_renewal: !attributes.paymentDetails?.optOutNextYearAutoRenewal,
      policy_id: attributes?.policyNumber ?? null,
      ss_order_metrics: attributes.paymentDetails?.encryptedMarginValue ?? null,
      previous_policy_id: attributes.gtmEvents?.previousPolicyId ?? "N/A",
      user_id: attributes.gtmEvents?.userId ?? null,
    },
    isReady: referenceId as unknown as boolean,
  });

  const singleAccordionItem = (
    title: string,
    content: string,
    isBoarder: boolean
  ) => {
    return (
      <>
        <div className="break-words">
          <h2 className="text-2xs font-normal leading-[150%] text-dark-text-functional-subtle">
            {title}
          </h2>
          <p className="text-dark-text-title md:text-l text-s font-bold md:leading-[28.75px] leading-[23px]">
            {content}
          </p>
        </div>
        {isBoarder && (
          <Divider
            color={selectedColorBrand.dark["border-strong-2"]}
            thickness={1}
            className="md:!my-spacing-m !my-spacing-s"
          />
        )}
      </>
    );
  };
  const accodionContent = () => {
    if (!referenceId)
      return (
        <div
          data-testid="notFound-testid"
          className="text-2xs font-normal leading-custom-size-lg text-dark-text-functional-subtle"
        >
          Loading..
        </div>
      );
    return (
      <>
        {singleAccordionItem(
          yourPolicyNameText,
          ` ${attributes?.organiser?.firstName} ${attributes?.organiser?.lastName}`,
          true
        )}

        {singleAccordionItem(
          orderTextDate,
          new Date().toLocaleDateString("en-GB"),
          true
        )}

        {singleAccordionItem(
          typeOfCoverText,
          attributes?.tripDetails?.policyType === "SINGLE_TRIP"
            ? "Single trip"
            : "Annual trip",
          true
        )}

        {singleAccordionItem(
          coverDatesText,
          `${rearrangeDateFormat(
            attributes?.tripDetails?.startDate
          )} - ${rearrangeDateFormat(attributes?.tripDetails?.policyEndDate)}`,
          true
        )}

        {singleAccordionItem(
          policyCostText,
          `£${attributes?.paymentDetails?.paymentOptions[0]?.cardPayment?.card?.price}`,
          true
        )}

        {singleAccordionItem(
          emailAdressText,
          attributes?.organiser?.email ?? "",
          true
        )}

        {singleAccordionItem(phoneNumberText, telephoneNumber ?? "", false)}
      </>
    );
  };

  return (
    <Accordion
      title={
        <h1 className="font-header-font md:text-3xl text-m leading-[27.6px] md:leading-[34.1px] font-[400] !text-dark-text-title">
          {yourPolicyDetailTitle}
        </h1>
      }
      content={accodionContent()}
      isexpanded={expanded}
      onClick={() => setExpanded((e) => !e)}
      icon={
        <ChevronDownIcon stroke={selectedColorBrand.dark["border-strong-2"]} />
      }
      className="!p-0"
      contentClassName="lg:mt-spacing-l md:mt-spacing-m mt-spacing-s "
    />
  );
}

export default ConfirmationPolicyDetails;
